import { FormTemplate } from "components/modal/templates";
import { formatDate } from "helpers/date";
import React from "react";
import { alert } from "services/alert";
import {
    periodList as classificationTypesPeriodList,
    singleList as classificationTypesSingleList,
} from "sources/books/classificationTypes";
import {
    createByRegistry,
    createUniversalTransferDocument,
    getBidsForRegistry,
} from "sources/books/universalTransferDocuments";
import { config, initialState } from "./config";

const parseIds = (value) =>
    `${value || ""}`
        .split(/[,\s]+/)
        .map((item) => parseInt(item))
        .filter((item) => !Number.isNaN(item));

class CreateUniversalTransferDocumentForm extends FormTemplate {
    close = (data = false) => {
        this.setState({ isLoading: false });
        this.props.back?.(data);
    };

    render = () => this.renderBody();

    bidIds = (state = this.state) => {
        const ids = state.sourceMode === "search" ? state.selectedBidIds : parseIds(state.manualBidIds);
        return [...new Set(ids)];
    };

    classificationScopeKey = (state = this.state) =>
        !state.registryId && this.bidIds(state).length === 1 ? "single" : "period";

    classificationTypesList = () =>
        this.classificationScopeKey() === "single" ? classificationTypesSingleList : classificationTypesPeriodList;

    setValue = ({ name, value }) => {
        const prevScopeKey = this.classificationScopeKey();
        const nextState = { ...this.state, [name]: value };
        const nextScopeKey = this.classificationScopeKey(nextState);
        const nextValue = {
            [name]: value,
            ...(prevScopeKey !== nextScopeKey ? { classificationTypeId: "" } : {}),
        };
        const isValid = this.isValidForm({ ...this.state, ...nextValue });
        this.setState({ ...nextValue, ...(isValid ? { isValid: true } : {}) });
    };

    setSelectedBidIds = (selectedBidIds) => {
        const prevScopeKey = this.classificationScopeKey();
        const nextState = { ...this.state, selectedBidIds };
        const nextScopeKey = this.classificationScopeKey(nextState);
        const nextValue = {
            selectedBidIds,
            ...(prevScopeKey !== nextScopeKey ? { classificationTypeId: "" } : {}),
        };
        const isValid = this.isValidForm({ ...this.state, ...nextValue });
        this.setState({ ...nextValue, ...(isValid ? { isValid: true } : {}) });
    };

    setSourceMode = (sourceMode) => {
        const prevScopeKey = this.classificationScopeKey();
        const nextState = {
            ...this.state,
            sourceMode,
            manualBidIds: "",
            selectedBidIds: [],
        };
        const nextScopeKey = this.classificationScopeKey(nextState);
        const nextValue = {
            sourceMode,
            manualBidIds: "",
            selectedBidIds: [],
            ...(prevScopeKey !== nextScopeKey ? { classificationTypeId: "" } : {}),
        };
        const isValid = this.isValidForm({ ...this.state, ...nextValue });
        this.setState({ ...nextValue, ...(isValid ? { isValid: true } : {}) });
    };

    searchBids = () => {
        const { counterpartyId, from, to } = this.state;
        if (!counterpartyId || !from || !to) {
            alert({ type: "warning", title: "Ошибка", message: "Выберите контрагента и период" });
            return;
        }
        this.setState({ loadingBids: true });
        getBidsForRegistry({
            counterpartyId,
            from: formatDate(from),
            to: formatDate(to),
        })
            .then((res) => {
                const bids = res || [];
                this.setState({ bids }, () =>
                    this.setSelectedBidIds(bids.filter((item) => item.isSuitableForRegistry).map((item) => item.id)),
                );
            })
            .finally(() => this.setState({ loadingBids: false }));
    };

    toggleBid = (id) => {
        const { selectedBidIds } = this.state;
        this.setSelectedBidIds(
            selectedBidIds.includes(id) ? selectedBidIds.filter((item) => item !== id) : [...selectedBidIds, id],
        );
    };

    sendRequest = () => {
        if (this.state.registryId) {
            return createByRegistry({
                registryId: this.state.registryId,
                classificationTypeId: this.state.classificationTypeId,
                type: this.state.type,
            });
        }

        const bidIds = this.bidIds();
        if (!bidIds.length) {
            alert({
                type: "warning",
                title: "Ошибка",
                message: this.state.sourceMode === "search" ? "Выберите заявки" : "Укажите ID заявок",
            });
            this.setState({ isLoading: false });
            return Promise.resolve(false);
        }
        if (bidIds.length > 1 && (!this.state.from || !this.state.to)) {
            alert({ type: "warning", title: "Ошибка", message: "Для УПД за период укажите даты периода" });
            this.setState({ isLoading: false });
            return Promise.resolve(false);
        }

        return createUniversalTransferDocument({
            bidIds,
            classificationTypeId: this.state.classificationTypeId,
            type: this.state.type,
            ...(bidIds.length > 1 ? { from: formatDate(this.state.from), to: formatDate(this.state.to) } : {}),
        });
    };

    renderBids = () => {
        const { bids, loadingBids, selectedBidIds } = this.state;
        return (
            <div className={loadingBids ? "loading loading--small" : ""}>
                {!!bids.length && (
                    <div className="table-responsive">
                        <table className="table table-sm table-bordered">
                            <thead>
                                <tr>
                                    <th />
                                    <th>ID</th>
                                    <th>Маршрут</th>
                                    <th>Сумма</th>
                                    <th>НДС</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bids.map((item) => (
                                    <tr key={item.id} className={!item.isSuitableForRegistry ? "text-muted" : ""}>
                                        <td>
                                            <input
                                                type="checkbox"
                                                checked={selectedBidIds.includes(item.id)}
                                                disabled={!item.isSuitableForRegistry}
                                                onChange={() => this.toggleBid(item.id)}
                                            />
                                        </td>
                                        <td>{item.id}</td>
                                        <td>
                                            {item.from} - {item.to}
                                        </td>
                                        <td>{item.price}</td>
                                        <td>
                                            {item.vat === null || item.vat === undefined ? "Без НДС" : `${item.vat}%`}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    };

    renderSourceMode = () => {
        const { sourceMode } = this.state;
        const sourceModes = [
            { value: "search", label: "Через поиск" },
            { value: "manual", label: "Вручную" },
        ];
        return (
            <div className="row form-group">
                <div className="offset-sm-4 offset-md-3 col-sm-8 col-md-9">
                    <div className="btn-group btn-group-sm">
                        {sourceModes.map((item) => (
                            <button
                                key={item.value}
                                type="button"
                                className={`btn ${sourceMode === item.value ? "btn-brand" : "btn-secondary"}`}
                                onClick={() => this.setSourceMode(item.value)}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    renderManualIds = () => (
        <>
            {this.renderField(this.props.config.manualBidIds)}
            <div className="row form-group mt-n3">
                <div className="offset-sm-4 offset-md-3 col-sm-8 col-md-9">
                    <small className="text-muted">Можно указать несколько ID через запятую или пробел.</small>
                </div>
            </div>
        </>
    );

    renderSearch = () => {
        const { counterpartyId } = this.props.config;
        return (
            <>
                {this.renderSelect(counterpartyId)}
                <div className="row">
                    <div className="col-sm-8 col-md-9 col-form-label-sm offset-sm-4 offset-md-3">
                        <div className="inline-btn inline-btn--without-icon mb-3" onClick={this.searchBids}>
                            Найти заявки
                        </div>
                    </div>
                </div>
                {this.renderBids()}
            </>
        );
    };

    renderBidsSource = () => {
        if (this.state.registryId) {
            return null;
        }
        const { from, to } = this.props.config;
        return (
            <div className="border-top pt-3 mt-2">
                <h6>Заявки</h6>
                {this.renderSourceMode()}
                {this.renderDateField(from)}
                {this.renderDateField(to)}
                {this.state.sourceMode === "search" ? this.renderSearch() : this.renderManualIds()}
            </div>
        );
    };

    renderBodyInner = () => {
        const { type, classificationTypeId, registryId } = this.props.config;
        return (
            <>
                {this.renderSelect(type)}
                {this.renderSelect({
                    ...classificationTypeId,
                    url: this.classificationTypesList(),
                    dataKey: this.classificationScopeKey(),
                })}
                {this.renderSelect(registryId)}
                {this.renderBidsSource()}
            </>
        );
    };

    renderActionSend = () => {
        const cname = "btn btn-brand btn-sm modal-window__footer-action";
        return <input type="submit" className={cname} value="Создать УПД" />;
    };
}

export default function ConfiguredCreateUniversalTransferDocumentForm(props) {
    return <CreateUniversalTransferDocumentForm {...props} config={config} initialState={initialState} />;
}
